'use client';

import { useTranslation } from 'react-i18next';

import { CLASSES_DA_FAIXA } from '@/app/mapa/helpers/coresDaOcupacao';
import { blocosDeMes } from '@/app/mapa/helpers/mesesDaFaixa';
import { IFaixasDeOcupacao } from '@/domain/configuracao/helpers/faixasOcupacao';
import { cn } from '@/lib/utils';

import { faixaDaSemanaDaVisao, ISemanaDaVisao } from '../helpers/visaoExecutiva';

/**
 * A folga entre duas semanas, em px: o MESMO valor da classe `gap-0.5` que as duas linhas
 * usam. O eixo dos meses alinha por esta conta, então ela não pode viver só na classe.
 */
const SEMANA_GAP_PX = 2;

/** O piso da largura de uma semana. Abaixo disso a faixa rola, em vez de sumir a semana. */
const SEMANA_MIN_PX = 6;

/** A altura da faixa: alta o bastante para a cor se ler, baixa para caber na linha. */
const FAIXA_H = 'h-4';

/** A largura exata de N semanas com a folga entre elas, que é o que um mês ocupa. */
function larguraDeSemanas(semanas: number): number {
  return semanas * SEMANA_MIN_PX + (semanas - 1) * SEMANA_GAP_PX;
}

interface FaixaDeSemanasProps {
  semanas: Array<ISemanaDaVisao>;
  /** Os cortes de cor em vigor (Configurações › Ocupação), os mesmos que o Mapa pinta. */
  faixas: IFaixasDeOcupacao;
  className?: string;
}

/**
 * As semanas do período de UMA célula, uma coluna por semana, na LARGURA TODA do bloco.
 *
 * As colunas repartem a largura em partes iguais (`flexGrow` com base zero), e não um
 * tamanho fixo: com tamanho fixo a faixa ficava espremida à esquerda e sobrava um vazio
 * grande à direita em todo período curto. É a mesma conta da faixa do Mapa, para as duas
 * leituras continuarem parecidas.
 *
 * Sai da leitura em voz alta (`aria-hidden`): o resumo escrito na linha de cima conta
 * exatamente o que ela desenha.
 */
export function FaixaDeSemanas({ semanas, faixas, className }: FaixaDeSemanasProps) {
  return (
    <div className={cn('flex w-full gap-0.5', className)} aria-hidden>
      {semanas.map((semana) => (
        <span
          key={semana.inicio}
          style={{ flexGrow: 1, flexBasis: 0, minWidth: `${SEMANA_MIN_PX}px` }}
          className={cn(
            'rounded-sm',
            FAIXA_H,
            CLASSES_DA_FAIXA[faixaDaSemanaDaVisao(semana, faixas)].fundo,
            // O contorno da semana de hoje, como a grade do Mapa a marca. É um `ring` e
            // não um `outline`: o `cn` (tailwind-merge) entende o `outline` pelado como
            // cor e o apaga quando vem um `outline-brand` depois, então a marca ficava
            // com largura e cor mas sem estilo nenhum, ou seja, invisível.
            semana.atual && 'ring-2 ring-brand',
          )}
        />
      ))}
    </div>
  );
}

interface EixoDosMesesProps {
  semanas: Array<ISemanaDaVisao>;
  className?: string;
}

/**
 * Os meses das semanas da faixa, embaixo dela. Sem ele ninguém sabe se o aglomerado de
 * vermelho está em novembro ou em janeiro, que é a primeira coisa que se pergunta.
 *
 * Cada mês cresce na PROPORÇÃO das semanas dele, com a folga interna como base: é a mesma
 * conta que as colunas fazem, então as duas linhas continuam alinhadas em qualquer
 * largura. O recuo lateral vem de fora, para o eixo começar onde a faixa começa.
 */
export function EixoDosMeses({ semanas, className }: EixoDosMesesProps) {
  const { t } = useTranslation('common');

  const meses = blocosDeMes(semanas);

  return (
    <div className={cn('flex w-full gap-0.5', className)}>
      {meses.map((bloco) => (
        <span
          key={bloco.chave}
          style={{
            flexGrow: bloco.semanas,
            flexBasis: `${(bloco.semanas - 1) * SEMANA_GAP_PX}px`,
            minWidth: `${larguraDeSemanas(bloco.semanas)}px`,
          }}
          className="truncate border-t border-border pt-1 text-[10px] uppercase tracking-wide text-foreground-subtle"
        >
          {t(`mapa.grade.mes.${bloco.mes}`)}
        </span>
      ))}
    </div>
  );
}
